import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  PiggyBank,
  RefreshCw,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import { PageHeader } from "../components/ui";
import { api } from "../lib/api";
import type { RenewalPricing, VoluntaryElection } from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";

function RenewalRow({
  r,
  onApprove,
  onReject,
}: {
  r: RenewalPricing;
  onApprove: () => void;
  onReject: () => void;
}) {
  const { t } = useTranslation();
  const flagged = r.status === "flagged_variance";
  return (
    <div
      className={`rounded-2xl border p-3 ${
        flagged
          ? "border-amber-300 bg-amber-50/50"
          : r.status === "approved"
          ? "border-emerald-300 bg-emerald-50/40"
          : r.status === "rejected"
          ? "border-rose-300 bg-rose-50/40"
          : "border-border-subtle bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-medium text-text-primary">
            {r.plan_id.slice(0, 8)}… · {r.renewal_year}
          </div>
          <div className="text-xs text-text-tertiary">
            {t("benefits.renewal_effective_from", "Effective from")}{" "}
            {r.effective_from}
          </div>
        </div>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-700">
          {r.status.replace(/_/g, " ")}
        </span>
      </div>
      <dl className="mt-2 grid grid-cols-3 gap-x-3 gap-y-1 text-xs text-text-secondary">
        <div>
          <dt className="text-text-tertiary">
            {t("benefits.renewal_old", "Old/mo")}
          </dt>
          <dd className="font-mono">{r.old_total_monthly_premium}</dd>
        </div>
        <div>
          <dt className="text-text-tertiary">
            {t("benefits.renewal_new", "New/mo")}
          </dt>
          <dd className="font-mono">{r.new_total_monthly_premium}</dd>
        </div>
        <div>
          <dt className="text-text-tertiary">
            {t("benefits.renewal_variance", "Variance")}
          </dt>
          <dd
            className={`font-mono ${
              flagged ? "text-amber-800" : "text-text-primary"
            }`}
          >
            {r.variance_pct}%
          </dd>
        </div>
      </dl>
      {flagged && (
        <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" />
          {t(
            "benefits.renewal_flag",
            "Variance above the threshold — Mutaba'aa flagged this for HR review.",
          )}
        </div>
      )}
      {(r.status === "proposed" || r.status === "flagged_variance") && (
        <div className="mt-3 flex gap-1.5">
          <button
            type="button"
            onClick={onApprove}
            className="rounded-lg bg-emerald-700 px-2 py-1 text-[11px] font-medium text-white hover:opacity-95"
          >
            {t("benefits.renewal_approve", "Approve")}
          </button>
          <button
            type="button"
            onClick={onReject}
            className="rounded-lg border border-rose-300 px-2 py-1 text-[11px] font-medium text-rose-700 hover:bg-rose-50"
          >
            {t("benefits.renewal_reject", "Reject")}
          </button>
        </div>
      )}
    </div>
  );
}

function ElectionRow({
  e,
  onPause,
  onCancel,
}: {
  e: VoluntaryElection;
  onPause: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-border-subtle bg-white p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-medium text-text-primary">
            {e.employee_id.slice(0, 8)}…
          </div>
          <div className="text-xs text-text-tertiary">
            {t("benefits.election_pct", "Election")} {e.election_pct}%
            · {t("benefits.election_match", "Match")}{" "}
            {e.employer_match_pct_of_election}%
            {e.employer_match_monthly_cap && (
              <> · cap {e.employer_match_monthly_cap} SAR/mo</>
            )}
          </div>
        </div>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-700">
          {e.status}
        </span>
      </div>
      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-text-secondary">
        <div>
          <dt className="text-text-tertiary">
            {t("benefits.election_from", "From")}
          </dt>
          <dd>{e.effective_from}</dd>
        </div>
        <div>
          <dt className="text-text-tertiary">
            {t("benefits.election_until", "Until")}
          </dt>
          <dd>{e.effective_until ?? "—"}</dd>
        </div>
        <div className="col-span-2">
          <dt className="text-text-tertiary">
            {t("benefits.election_fund", "Fund manager")}
          </dt>
          <dd>{e.fund_manager ?? "—"}</dd>
        </div>
      </dl>
      {e.status === "active" && (
        <div className="mt-3 flex gap-1.5">
          <button
            type="button"
            onClick={onPause}
            className="rounded-lg border border-border-subtle px-2 py-1 text-[11px] text-text-secondary hover:bg-surface-sunken"
          >
            {t("benefits.election_pause", "Pause")}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-rose-300 px-2 py-1 text-[11px] font-medium text-rose-700 hover:bg-rose-50"
          >
            {t("benefits.election_cancel", "Cancel")}
          </button>
        </div>
      )}
    </div>
  );
}

export function RenewalAndRetirementPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { legalEntityId, gate } = useLegalEntityGate();

  const renewals = useQuery({
    queryKey: ["renewal-pricing", legalEntityId],
    queryFn: () => api.listRenewalPricing(legalEntityId ?? ""),
    enabled: !!legalEntityId,
  });

  const elections = useQuery({
    queryKey: ["voluntary-retirement", legalEntityId],
    queryFn: () => api.listVoluntaryElections(legalEntityId ?? ""),
    enabled: !!legalEntityId,
  });

  const renApprove = useMutation({
    mutationFn: (id: string) =>
      api.approveRenewalPricing(id, {
        approver_clerk_user_id: "hr_director",
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["renewal-pricing"] }),
  });
  const renReject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.rejectRenewalPricing(id, {
        rejector_clerk_user_id: "hr_director",
        reason,
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["renewal-pricing"] }),
  });
  const elPause = useMutation({
    mutationFn: (id: string) => api.pauseVoluntaryElection(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["voluntary-retirement"] }),
  });
  const elCancel = useMutation({
    mutationFn: (id: string) => api.cancelVoluntaryElection(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["voluntary-retirement"] }),
  });

  if (gate) return gate;

  return (
    <div className="space-y-4">
      <PageHeader
        title={t(
          "benefits.renewal_title",
          "Renewals + voluntary retirement",
        )}
        subtitle={t(
          "benefits.renewal_subtitle",
          "Annual renewal pricing review (Mutaba'aa flags >20% variance) + per-employee voluntary retirement elections (Q1=YES — ships in v1).",
        )}
      />

      <h2 className="flex items-center gap-2 text-base font-semibold text-text-primary">
        <RefreshCw className="h-4 w-4" />
        {t("benefits.renewal_section", "Renewal pricing proposals")}
      </h2>

      {renewals.isLoading && <Loading />}
      {renewals.isError && (
        <ErrorPanel message={(renewals.error as Error).message} />
      )}
      {renewals.data && renewals.data.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border-subtle p-4 text-center text-sm text-text-tertiary">
          {t(
            "benefits.renewal_empty",
            "No renewal proposals yet — pricing changes come in via HR upload.",
          )}
        </div>
      )}
      {renewals.data && renewals.data.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {renewals.data.map((r) => (
            <RenewalRow
              key={r.id}
              r={r}
              onApprove={() => renApprove.mutate(r.id)}
              onReject={() => {
                const reason = window.prompt(
                  t(
                    "benefits.renewal_reject_prompt",
                    "Rejection reason:",
                  ),
                );
                if (reason && reason.trim().length > 0)
                  renReject.mutate({ id: r.id, reason });
              }}
            />
          ))}
        </div>
      )}

      <h2 className="mt-4 flex items-center gap-2 text-base font-semibold text-text-primary">
        <PiggyBank className="h-4 w-4" />
        {t(
          "benefits.election_section",
          "Voluntary retirement elections",
        )}
      </h2>

      {elections.isLoading && <Loading />}
      {elections.isError && (
        <ErrorPanel message={(elections.error as Error).message} />
      )}
      {elections.data && elections.data.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border-subtle p-4 text-center text-sm text-text-tertiary">
          {t(
            "benefits.election_empty",
            "No employees have elected voluntary retirement contributions yet.",
          )}
        </div>
      )}
      {elections.data && elections.data.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {elections.data.map((e) => (
            <ElectionRow
              key={e.id}
              e={e}
              onPause={() => elPause.mutate(e.id)}
              onCancel={() => elCancel.mutate(e.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
